/** Composant pour afficher une liste accordéon des dossiers avec sélection */
import React, { FC, useState, useMemo, useCallback, useEffect } from "react";

import { useFolderSearch } from "@keepeek/commons";
import {
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
} from "@mui/material";

import { KIcon } from "../../../../../../components/common/KIcon";

type ListFirstLevelFoldersWidgetProps = {
  columnId: string;
  columnData: any[];
  formData: any;
  onChange: (data: any) => void;
  t: any;
};

export const ListFirstLevelFoldersWidget: FC<ListFirstLevelFoldersWidgetProps> = function ({
  columnId,
  columnData,
  formData,
  t,
  onChange,
}) {
  const [childrenMap, setChildrenMap] = useState<Map<number, any[]>>(new Map());
  const [searchText, setSearchText] = useState<string>("");

  // Le rootFolderId définit le point de départ
  const rootFolderId = formData?.rootFolderId;

  // Initialiser selectedFolders depuis columnData
  const initialSelectedFolders = useMemo(() => {
    const selected = new Set<number>();
    if (Array.isArray(columnData)) {
      columnData.forEach((item: any) => {
        if (item?.firstLevelFolderId) {
          selected.add(item.firstLevelFolderId);
        }
        if (Array.isArray(item?.folderIds)) {
          item.folderIds.forEach((id: number) => selected.add(id));
        }
      });
    }
    return selected;
  }, [columnData]);

  const [selectedFolders, setSelectedFolders] = useState<Set<number>>(initialSelectedFolders);

  // Charger les dossiers avec useFolderSearch
  // Si rootFolderId existe, charge ses enfants directs
  // Sinon, charge les dossiers racine du plan de classement
  const { folderTrees, loading } = useFolderSearch({
    folderIds: rootFolderId && rootFolderId > 0 ? [rootFolderId] : undefined,
    withChildrenList: true,
  });

  // Extraire les dossiers à afficher
  const folders = useMemo(() => {
    if (!folderTrees || folderTrees.length === 0) return [];

    if (rootFolderId && rootFolderId > 0) {
      // Retourner les enfants du rootFolderId
      return folderTrees[0]?.children || [];
    } else {
      // Retourner les dossiers racine du plan de classement
      return folderTrees.map((tree) => tree.folder);
    }
  }, [folderTrees, rootFolderId]);

  // Filtrer les dossiers en fonction du texte de recherche
  const filteredFolders = useMemo(() => {
    if (!searchText.trim()) return folders;

    const searchLower = searchText.toLowerCase().trim();
    return folders.filter((folder) => {
      // Vérifier si le titre du parent correspond
      const parentMatches = folder.title?.toLowerCase().includes(searchLower);

      // Vérifier si au moins un enfant correspond
      const children = childrenMap.get(folder.id) || [];
      const childMatches = children.some((child) =>
        child.title?.toLowerCase().includes(searchLower),
      );

      return parentMatches || childMatches;
    });
  }, [folders, searchText, childrenMap]);

  // Synchroniser l'état initial avec columnData
  useEffect(() => {
    setSelectedFolders(initialSelectedFolders);
  }, [initialSelectedFolders]);

  // Callback pour enregistrer les enfants d'un parent
  const handleChildrenLoaded = useCallback((parentId: number, children: any[]) => {
    setChildrenMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(parentId, children);
      return newMap;
    });
  }, []);

  // Mettre à jour columnData quand selectedFolders change
  useEffect(() => {
    // Parcourir les dossiers dans l'ordre du backend et construire les données formatées
    const formattedData = folders
      .map((folder) => {
        const firstLevelFolderId = folder.id;
        // Récupérer les enfants de ce dossier
        const children = childrenMap.get(firstLevelFolderId) || [];
        const childrenIds = children.map((child) => child.id);
        // Filtrer pour garder uniquement les enfants sélectionnés
        const selectedChildren = childrenIds.filter((id) => selectedFolders.has(id));

        // Retourner les données si :
        // - au moins un enfant est sélectionné OU
        // - le dossier parent lui-même est sélectionné (dossier sans enfants ou parent coché)
        if (selectedChildren.length > 0 || selectedFolders.has(firstLevelFolderId)) {
          return {
            firstLevelFolderId,
            folderIds: selectedChildren,
          };
        }
        return null;
      })
      .filter((item): item is { firstLevelFolderId: number; folderIds: number[] } => item !== null);

    onChange({
      ...formData,
      [columnId]: formattedData,
    });
  }, [selectedFolders, childrenMap, columnId, folders, formData, onChange]);

  // Gérer la sélection d'un dossier parent
  const toggleParentSelection = useCallback(
    (parentId: number) => {
      setSelectedFolders((prev) => {
        const newSet = new Set(prev);
        const children = childrenMap.get(parentId) || [];
        const childrenIds = children.map((child) => child.id);

        if (newSet.has(parentId)) {
          // Désélectionner parent et tous ses enfants
          newSet.delete(parentId);
          childrenIds.forEach((childId) => newSet.delete(childId));
        } else {
          // Sélectionner parent et tous ses enfants
          newSet.add(parentId);
          childrenIds.forEach((childId) => newSet.add(childId));
        }

        return newSet;
      });
    },
    [childrenMap],
  );

  // Gérer la sélection d'un dossier enfant
  const toggleChildSelection = useCallback(
    (childId: number, parentId: number) => {
      setSelectedFolders((prev) => {
        const newSet = new Set(prev);
        const children = childrenMap.get(parentId) || [];
        const childrenIds = children.map((child) => child.id);

        if (newSet.has(childId)) {
          // Si l'enfant est déjà sélectionné, désélectionner l'enfant
          newSet.delete(childId);
        } else {
          // Si l'enfant n'est pas sélectionné, sélectionner l'enfant
          newSet.add(childId);
        }

        // Vérifier si tous les enfants sont sélectionnés
        const allChildrenSelected = childrenIds.every((id) => newSet.has(id));
        if (allChildrenSelected && childrenIds.length > 0) {
          // Si tous les enfants sont sélectionnés, sélectionner le parent
          newSet.add(parentId);
        } else {
          newSet.delete(parentId);
        }

        return newSet;
      });
    },
    [childrenMap],
  );

  // Calculer l'état d'un dossier parent (checked, indeterminate)
  const getParentCheckState = useCallback(
    (parentId: number) => {
      // Obtenir les IDs des enfants du parent
      const children = childrenMap.get(parentId) || [];
      const childrenIds = children.map((child) => child.id);
      if (childrenIds.length === 0) {
        // Si le dossier n'a pas d'enfants, retourner l'état de sélection du parent
        return { checked: selectedFolders.has(parentId), indeterminate: false };
      }
      // Compter les enfants sélectionnés
      const selectedChildrenCount = childrenIds.filter((id) => selectedFolders.has(id)).length;

      if (selectedChildrenCount === 0) {
        //Si le parent a des enfants et aucun enfant n'est sélectionné, retourner l'état de sélection du parent
        return { checked: false, indeterminate: false };
      } else if (selectedChildrenCount === childrenIds.length) {
        // Si tous les enfants sont sélectionnés, retourner l'état de sélection du parent
        return { checked: true, indeterminate: false };
      } else {
        // Si certains enfants sont sélectionnés, retourner l'état de sélection du parent
        return { checked: false, indeterminate: true };
      }
    },
    [childrenMap, selectedFolders],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (folders.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ padding: 2 }}>
        {t("components.kThematicWidget.noFolders.label")}
      </Typography>
    );
  }

  return (
    <Box>
      {/* Barre de recherche */}
      <Box sx={{ padding: 2, paddingBottom: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <KIcon className="fa-search" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Message si aucun résultat */}
      {filteredFolders.length === 0 && searchText.trim() && (
        <Typography variant="body2" color="text.secondary" sx={{ padding: 2, textAlign: "center" }}>
          {t("components.kThematicWidget.noResults.label") || "Aucun dossier trouvé"}
        </Typography>
      )}

      {filteredFolders.map((folder) => {
        const subFolderCount = folder.hasChildren ? folder.subFolderCount || 0 : 0;
        const parentCheckState = getParentCheckState(folder.id);

        return (
          <Accordion
            key={folder.id}
            disabled={false}
            expanded={folder.hasChildren ? undefined : false}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              "&:not(:last-child)": {
                borderBottom: 0,
              },
              "&:before": {
                display: "none",
              },
              // Empêcher le curseur pointer sur les dossiers sans enfants
              "& .MuiAccordionSummary-root": folder.hasChildren
                ? {}
                : {
                    cursor: "pointer !important",
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      display: "none",
                    },
                  },
            }}
          >
            <AccordionSummary
              expandIcon={folder.hasChildren ? <KIcon className="fa-chevron-down" /> : null}
              sx={{
                backgroundColor: "#f5f5f5",
                "&:hover": {
                  backgroundColor: "#eeeeee",
                },
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                },
              }}
            >
              <Checkbox
                size="small"
                checked={parentCheckState.checked}
                indeterminate={parentCheckState.indeterminate}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleParentSelection(folder.id);
                }}
              />

              <Typography
                variant="body1"
                sx={{
                  flex: 1,
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {folder.title || `Dossier ${folder.id}`}
              </Typography>

              {folder.hasChildren && (
                <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                  {subFolderCount} {t("components.kThematicWidget.subFolders.label") || "dossiers"}
                </Typography>
              )}
            </AccordionSummary>

            {folder.hasChildren && (
              <AccordionDetails sx={{ p: 0, backgroundColor: "#fafafa" }}>
                <SubFolderList
                  folderId={folder.id}
                  selectedFolders={selectedFolders}
                  onToggleSelect={toggleChildSelection}
                  onChildrenLoaded={handleChildrenLoaded}
                  searchText={searchText}
                  t={t}
                />
              </AccordionDetails>
            )}
          </Accordion>
        );
      })}
    </Box>
  );
};

// Composant pour afficher les sous-dossiers
const SubFolderList: FC<{
  folderId: number;
  selectedFolders: Set<number>;
  onToggleSelect: (childId: number, parentId: number) => void;
  onChildrenLoaded: (parentId: number, children: any[]) => void;
  searchText: string;
  t: any;
}> = function ({ folderId, selectedFolders, onToggleSelect, onChildrenLoaded, searchText, t }) {
  const hasNotifiedRef = React.useRef(false);

  const { folderTrees, loading } = useFolderSearch({
    folderIds: [folderId],
    withChildrenList: true,
  });

  const subFolders = useMemo(() => {
    if (!folderTrees || folderTrees.length === 0) return [];
    return folderTrees[0]?.children || [];
  }, [folderTrees]);

  // Filtrer les sous-dossiers en fonction du texte de recherche
  const filteredSubFolders = useMemo(() => {
    if (!searchText.trim()) return subFolders;

    const searchLower = searchText.toLowerCase().trim();
    return subFolders.filter((subFolder) => subFolder.title?.toLowerCase().includes(searchLower));
  }, [subFolders, searchText]);

  // Notifier le parent quand les enfants sont chargés (une seule fois)
  useEffect(() => {
    if (subFolders.length > 0 && !hasNotifiedRef.current) {
      onChildrenLoaded(folderId, subFolders);
      hasNotifiedRef.current = true;
    }
  }, [subFolders, folderId, onChildrenLoaded]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (subFolders.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ padding: 1, pl: 6 }}>
        {t("components.kThematicWidget.noSubFolders.label") || "Aucun sous-dossier"}
      </Typography>
    );
  }

  if (filteredSubFolders.length === 0 && searchText.trim()) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ padding: 1, pl: 6 }}>
        {t("components.kThematicWidget.noResults.label") || "Aucun dossier trouvé"}
      </Typography>
    );
  }

  return (
    <>
      {filteredSubFolders.map((subFolder) => {
        const isSelected = selectedFolders.has(subFolder.id);

        return (
          <Box
            key={subFolder.id}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 1,
              pl: 6,
              borderBottom: "1px solid #e0e0e0",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => onToggleSelect(subFolder.id, folderId)}
            />
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {subFolder.title || `Dossier ${subFolder.id}`}
            </Typography>
          </Box>
        );
      })}
    </>
  );
};
